import { useState } from 'react';
import { ChevronDown, ChevronTop, Warning } from 'assets/icons';
import Tooltip from 'components/tooltips/Tooltips';
import { remunerationSections } from 'helper/remunerationConditions';

/**
 * The company's remuneration terms, read-only (JBWEB-1208).
 *
 * The contract preview shows what the contract was drawn from, so nothing here
 * opens an editor: the panel shows one modal at a time, and a pencil that
 * could not open anything would be worse than no pencil.
 *
 * The sentences come from helper/remunerationConditions, shared with the
 * company panel — the same settings described twice in two wordings would
 * drift.
 */
const Collapsible = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={'border rounded-lg overflow-hidden'}>
      <button
        type={'button'}
        className={'w-full py-3 px-4 flex justify-between items-center hover:bg-gray-50'}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span className={'font-semi-bold text-gray-900'}>{title}</span>
        {open ? (
          <ChevronTop wh={20} color={'#374151'} />
        ) : (
          <ChevronDown wh={20} color={'#374151'} />
        )}
      </button>
      {open && <div className={'border-t divide-y'}>{children}</div>}
    </div>
  );
};

const RemunerationSummary = ({ companyInfo }) => {
  const sections = remunerationSections(companyInfo);
  const bonuses = companyInfo?.bonusesAndAllowances?.bonuses ?? [];

  return (
    <div className={'flex flex-col gap-3'}>
      <Collapsible title={'Conditions de rémunération'}>
        {sections.map((section) => (
          <div key={section.key} className={'py-3 px-4 flex justify-between gap-2'}>
            <div className={'min-w-0'}>
              <div className={'text-gray-900'}>{section.title}</div>
              {section.intro && (
                <div className={'text-gray-500'}>{section.intro}</div>
              )}
              {section.bullets.length > 0 && (
                <ul className={'list-disc list-inside text-gray-500'}>
                  {section.bullets.map((line, index) => (
                    <li key={index}>{line}</li>
                  ))}
                </ul>
              )}
              {section.extra && (
                <div className={'text-gray-500'}>{section.extra}</div>
              )}
            </div>
            {/* Same warning the company panel shows, and for the same reason:
                a section still on its defaults was never agreed with this
                client, which is worth knowing while reading their contract. */}
            {section.isDefault && (
              <Tooltip text={'Données par défaut'} placement={'top'} zIndex={60}>
                <span className={'my-auto shrink-0 cursor-help'}>
                  <Warning wh={20} color={'#975612'} />
                </span>
              </Tooltip>
            )}
          </div>
        ))}
      </Collapsible>

      <Collapsible title={'Primes & indemnités'} defaultOpen={false}>
        {bonuses.length === 0 ? (
          <div className={'py-3 px-4 text-gray-500'}>
            Aucune prime ni indemnité.
          </div>
        ) : (
          bonuses.map((bonus, index) => (
            <div
              key={bonus._id ?? index}
              className={'py-3 px-4 flex justify-between gap-2'}
            >
              <span className={'text-gray-900 min-w-0 truncate'}>
                {bonus.name?.name?.trim() ?? bonus.name ?? '—'}
              </span>
              <span className={'text-gray-500 shrink-0'}>
                {bonus.unit?.name ?? bonus.unit ?? ''}
                {bonus.payed !== undefined && bonus.payed !== null
                  ? ` · ${bonus.payed}`
                  : ''}
              </span>
            </div>
          ))
        )}
      </Collapsible>
    </div>
  );
};

export default RemunerationSummary;
